import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { QUERY_STATE } from '../../constants';
import type { MonitoringListQuery } from '../../openapi/operations/monitoringList';
import { useMonitoringListResource } from '../../openapi/operations/monitoringList';
import type { MonitoringLogRowResponse } from '../../openapi/types';
import { Level02aEnum } from '../../openapi/types/Level02aEnum';

/** Varování se mezi chyby nepočítá. */
const ERROR_LEVELS = [Level02aEnum.ERROR, Level02aEnum.CRITICAL];

export type MonitoringSubjectKey =
  | 'doc'
  | 'epsi_doc'
  | 'order'
  | 'epsi_order'
  | 'urceny_editor_zps';

/** Volající bez role ADMIN musí poslat právě jeden filtr subjektu, jinak dostane 403. */
export type MonitoringSubject = {
  [Key in MonitoringSubjectKey]: Record<Key, number>;
}[MonitoringSubjectKey];

export type MonitoringTimeline = {
  queryState: QUERY_STATE;
  rows: MonitoringLogRowResponse[];
  /** Počet záznamů na serveru, ne počet načtených. */
  count: number;
  /** Nula bez `withErrors`. */
  errorCount: number;
  /** Nula bez `withWarnings`. */
  warningCount: number;
  latestError: MonitoringLogRowResponse | null;
  hasMore: boolean;
  /** Kolik záznamů dotáhne `loadMore`; poslední stránka bývá kratší. */
  nextPageCount: number;
  isLoadingMore: boolean;
  /** Dorazila zpráva o tomto objektu, ale načtené stránky držíme - obnovení je na uživateli. */
  isStale: boolean;
  loadMore(): void;
  refresh(): void;
  markStale(): void;
};

/** Každá volba je dotaz navíc, tak jen pro volající, kteří ten údaj opravdu zobrazují. */
export type MonitoringTimelineOptions = {
  withErrors?: boolean;
  withWarnings?: boolean;
};

export const useMonitoringTimeline = (
  subject: MonitoringSubject | null,
  { withErrors, withWarnings }: MonitoringTimelineOptions = {},
): MonitoringTimeline => {
  const [isStale, setIsStale] = useState(false);

  const errorQuery: MonitoringListQuery | null = useMemo(
    () => (subject && withErrors ? { ...subject, level: ERROR_LEVELS, page_size: 1 } : null),
    [subject, withErrors],
  );

  const warningQuery: MonitoringListQuery | null = useMemo(
    () =>
      subject && withWarnings ? { ...subject, level: [Level02aEnum.WARNING], page_size: 1 } : null,
    [subject, withWarnings],
  );

  // Bez `page_size`: délku stránky drží server, FE ji jen zjistí z první odpovědi.
  const { queryState, data, fetchMore, refetch } = useMonitoringListResource(subject);
  const errorResource = useMonitoringListResource(errorQuery);
  const warningResource = useMonitoringListResource(warningQuery);
  const refetchErrors = errorResource.refetch;
  const refetchWarnings = warningResource.refetch;

  const rows = useMemo(() => data?.results ?? [], [data]);
  const count = data?.count ?? 0;
  const nextPage = data?.next ?? null;
  const hasMore = Boolean(nextPage);
  const isLoadingMore = queryState === QUERY_STATE.WAITING && rows.length > 0;

  const pageLengthRef = useRef(0);
  // Po `fetchMore` už `rows` drží víc stránek, tak se délka první z nich zapamatuje.
  if (!pageLengthRef.current) pageLengthRef.current = rows.length;

  // Aby `markStale` nemusel viset na načtených řádcích a zůstal stabilní pro SSE handler.
  const hasLoadedMoreRef = useRef(false);

  useEffect(() => {
    setIsStale(false);
    pageLengthRef.current = 0;
    hasLoadedMoreRef.current = false;
  }, [subject]);

  const nextPageCount = hasMore
    ? Math.min(pageLengthRef.current, Math.max(count - rows.length, 1))
    : 0;

  const loadMore = useCallback(() => {
    if (!nextPage) return;
    if (queryState === QUERY_STATE.WAITING) return;
    hasLoadedMoreRef.current = true;
    fetchMore(nextPage);
  }, [fetchMore, nextPage, queryState]);

  const refresh = useCallback(() => {
    setIsStale(false);
    refetch();
    refetchErrors();
    refetchWarnings();
  }, [refetch, refetchErrors, refetchWarnings]);

  const markStale = useCallback(() => {
    // Nad první stránkou není co ztratit, tak se osa obnoví rovnou.
    if (hasLoadedMoreRef.current) {
      setIsStale(true);
      return;
    }
    refetch();
    refetchErrors();
    refetchWarnings();
  }, [refetch, refetchErrors, refetchWarnings]);

  return useMemo(
    () => ({
      queryState,
      rows,
      count,
      errorCount: errorResource.data?.count ?? 0,
      warningCount: warningResource.data?.count ?? 0,
      latestError: errorResource.data?.results?.[0] ?? null,
      hasMore,
      nextPageCount,
      isLoadingMore,
      isStale,
      loadMore,
      refresh,
      markStale,
    }),
    [
      count,
      errorResource.data,
      warningResource.data,
      hasMore,
      isLoadingMore,
      isStale,
      loadMore,
      markStale,
      nextPageCount,
      queryState,
      rows,
      refresh,
    ],
  );
};
